import { EMPLOYEE_ROLE_LABELS, NO_ROLE_LABEL } from "@etudescarre/constants";
import { Badge, Button, Combobox, cn, Modal, Select } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { PencilIcon, PlusIcon, TrashIcon, XIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass as inputClass } from "@/lib/utils/cell-input";
import {
  confirmDiscardDraft,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import {
  AdminListPagination,
  AdminListSearch,
  fetchAllCompanies,
  useAdminListQuery,
} from "./components";
import { EmployeeForm } from "./employee-form";

type Employee = {
  id: string;
  companyId: number;
  email: string | null;
  emailVerified: boolean;
  firstName: string | null;
  lastName: string | null;
  role: "root" | "admin" | "user" | null;
  isActive: boolean;
  lastLogin: string | null;
  dateJoined: string | null;
  createdAt: string;
  company: {
    id: number;
    name: string;
  };
};

const roleColors: Record<
  "root" | "admin" | "user",
  "purple" | "blue" | "default"
> = {
  root: "purple",
  admin: "blue",
  user: "default",
};

/** Directly-editable employee fields staged in draft edit mode. Role null =
 * contact-only, no access to the tool. */
type EmployeeDraft = {
  firstName: string;
  lastName: string;
  email: string;
  companyId: number;
  role: "admin" | "user" | null;
  isActive: boolean;
};

type NewEmployeeRow = {
  tempId: string;
  // Whether the user has manually picked a role: once true, the
  // email-domain default preselection stops overriding it.
  roleTouched: boolean;
} & EmployeeDraft;

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

// A newly created collaborator's role is preselected from their email
// domain — Admin for @chantiercarre.fr, User otherwise — and stays
// editable afterwards.
const ADMIN_EMAIL_DOMAIN = "@chantiercarre.fr";
function defaultRoleForEmail(email: string): "admin" | "user" {
  return email.trim().toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN)
    ? "admin"
    : "user";
}

const NO_ROLE_VALUE = "none";
function roleSelectValue(role: "admin" | "user" | null): string {
  return role ?? NO_ROLE_VALUE;
}
function parseRoleSelectValue(
  value: string | undefined,
): "admin" | "user" | null {
  return value === "admin" || value === "user" ? value : null;
}

const assignableRoleOptions = [
  { label: EMPLOYEE_ROLE_LABELS.admin, value: "admin" },
  { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
  { label: `${NO_ROLE_LABEL} (contact)`, value: NO_ROLE_VALUE },
];

const statusOptions = [
  { label: "Actif", value: "1" },
  { label: "Inactif", value: "0" },
];

/**
 * Screen 902 — employees directory as an inline-editable draft table
 * (same pattern as screens 904/909/910): name, email, company, role and
 * status are edited in place and staged until "Enregistrer"; "+ Ajouter"
 * rows create new employees. The pencil keeps the drawer for the fields
 * without a column (date d'entrée).
 */
export function EmployeesList() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<
    Employee | undefined
  >();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterCompany, setFilterCompany] = useState<string[]>([]);
  const [filterRole, setFilterRole] = useState<string[]>([]);

  // Draft edit mode: pending edits keyed by employee id, plus not-yet-saved
  // new rows. Highlighted until Enregistrer/Annuler.
  const [draft, setDraft] = useState<Map<string, EmployeeDraft>>(new Map());
  const [newRows, setNewRows] = useState<NewEmployeeRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Every company (not only those that already have employees), for the
  // inline Société select.
  const [allCompanyOptions, setAllCompanyOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const fetchCompanies = useCallback(async () => {
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setAllCompanyOptions(
        companies.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      );
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const fetcher = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search?: string;
      page: number;
      pageSize: number;
    }) => {
      const { data, error } = await apiClient.GET("/api/admin/employees", {
        params: {
          query: {
            search: search ?? undefined,
            page,
            pageSize,
          },
        },
      });
      if (error) {
        throw new Error("Erreur lors du chargement des employés");
      }
      return {
        items: data.data.employees,
        total: data.data.total,
      };
    },
    [],
  );

  const {
    items: allEmployees,
    total,
    loading,
    error,
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    setPageSize,
    refetch,
  } = useAdminListQuery<Employee>(fetcher, "employees");

  // Client-side filters applied post-fetch
  const filteredEmployees = useMemo(() => {
    return allEmployees.filter((emp) => {
      if (
        filterCompany.length > 0 &&
        !filterCompany.includes(String(emp.company.id))
      ) {
        return false;
      }
      if (
        filterRole.length > 0 &&
        !filterRole.includes(emp.role ?? NO_ROLE_VALUE)
      ) {
        return false;
      }
      return true;
    });
  }, [allEmployees, filterCompany, filterRole]);

  const companyFilterOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const emp of allEmployees) {
      unique.set(emp.company.id, emp.company.name);
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [allEmployees]);

  const roleOptions = [
    { label: EMPLOYEE_ROLE_LABELS.root, value: "root" },
    { label: EMPLOYEE_ROLE_LABELS.admin, value: "admin" },
    { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
    { label: `${NO_ROLE_LABEL} (contact)`, value: NO_ROLE_VALUE },
  ];

  const isDirty = draft.size > 0 || newRows.length > 0;
  useUnsavedChangesGuard(isDirty);

  const serverDraft = (employee: Employee): EmployeeDraft => ({
    firstName: employee.firstName ?? "",
    lastName: employee.lastName ?? "",
    email: employee.email ?? "",
    companyId: employee.companyId,
    role: employee.role === "root" ? "admin" : employee.role,
    isActive: employee.isActive,
  });

  const getDraft = (employee: Employee): EmployeeDraft =>
    draft.get(employee.id) ?? serverDraft(employee);

  const isDirtyRow = (employee: Employee) => draft.has(employee.id);

  const onFieldChange = (employee: Employee, patch: Partial<EmployeeDraft>) => {
    setDraft((prev) => {
      const current = prev.get(employee.id) ?? serverDraft(employee);
      const next = { ...current, ...patch };
      const server = serverDraft(employee);
      const sameAsServer =
        next.firstName === server.firstName &&
        next.lastName === server.lastName &&
        next.email === server.email &&
        next.companyId === server.companyId &&
        next.role === server.role &&
        next.isActive === server.isActive;
      const updated = new Map(prev);
      if (sameAsServer) updated.delete(employee.id);
      else updated.set(employee.id, next);
      return updated;
    });
  };

  const onNewRowChange = (tempId: string, patch: Partial<NewEmployeeRow>) => {
    setNewRows((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
    );
  };

  const cancelDraft = () => {
    setDraft(new Map());
    setNewRows([]);
  };

  const save = async () => {
    // Email is optional: a collaborator can be added
    // without a login/contact email, filled in later. But a role grants
    // tool access, which requires login credentials, so a role can't be
    // assigned without one.
    const incomplete = [...draft.values(), ...newRows].some(
      (d) => !d.firstName.trim() || !d.lastName.trim() || !d.companyId,
    );
    if (incomplete) {
      toaster.error({
        title: "Erreur",
        description:
          "Prénom, nom et société sont requis pour chaque collaborateur.",
      });
      return;
    }
    const missingEmailForRole = [...draft.values(), ...newRows].some(
      (d) => d.role && !d.email.trim(),
    );
    if (missingEmailForRole) {
      toaster.error({
        title: "Erreur",
        description: "Le mail de login est requis pour attribuer un rôle.",
      });
      return;
    }

    setIsSaving(true);
    const failedDraftIds = new Set<string>();
    const failedNewRows: NewEmployeeRow[] = [];

    for (const [id, d] of draft.entries()) {
      const employee = allEmployees.find((e) => e.id === id);
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/employees/{id}",
        {
          params: { path: { id } },
          body: {
            companyId: d.companyId,
            email: d.email.trim() || null,
            firstName: d.firstName.trim(),
            lastName: d.lastName.trim(),
            // Root role is never assignable from this screen; existing
            // root rows keep their role (their Rôle cell is read-only).
            role: employee?.role === "root" ? undefined : d.role,
            isActive: d.isActive,
            dateJoined: employee?.dateJoined ?? null,
          },
        },
      );
      if (apiError) failedDraftIds.add(id);
    }

    for (const row of newRows) {
      const { error: apiError } = await apiClient.POST("/api/admin/employees", {
        body: {
          companyId: row.companyId,
          email: row.email.trim() || undefined,
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          role: row.role ?? undefined,
          isActive: row.isActive,
        },
      });
      if (apiError) failedNewRows.push(row);
    }

    setIsSaving(false);
    if (failedDraftIds.size > 0 || failedNewRows.length > 0) {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de certaines modifications",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
    }
    // Keep the failed entries staged so the user can correct and retry
    // instead of losing what they typed.
    setDraft((prev) => {
      const next = new Map<string, EmployeeDraft>();
      for (const id of failedDraftIds) {
        const value = prev.get(id);
        if (value) next.set(id, value);
      }
      return next;
    });
    setNewRows(failedNewRows);
    refetch();
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setDrawerOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setDeletingEmployee(employee);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingEmployee) return;

    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/employees/{id}",
      {
        params: { path: { id: deletingEmployee.id } },
      },
    );

    if (apiError) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression de l'employé",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Employé supprimé avec succès",
    });

    setDeleteModalOpen(false);
    setDeletingEmployee(null);
    setIsDeleting(false);
    refetch();
  };

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Employés
          </h2>
          <div className="flex items-center gap-2">
            {isDirty && (
              <>
                <span className="text-sm text-amber-600 dark:text-amber-400">
                  Modifications non enregistrées
                </span>
                <Button
                  outline
                  onClick={() => void confirmDiscardDraft(cancelDraft)}
                  disabled={isSaving}
                >
                  Annuler
                </Button>
                <Button onClick={save} disabled={isSaving}>
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </>
            )}
            <Button
              onClick={() =>
                setNewRows((prev) => [
                  ...prev,
                  {
                    tempId: generateTempId(),
                    firstName: "",
                    lastName: "",
                    email: "",
                    companyId: 0,
                    role: "user",
                    roleTouched: false,
                    isActive: true,
                  },
                ])
              }
            >
              <PlusIcon data-slot="icon" className="size-4" />
              Ajouter
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <AdminListSearch
              value={search}
              onChange={setSearch}
              placeholder="Rechercher par nom ou email..."
              label="Recherche"
              id="employees-search"
            />
            <Combobox
              options={companyFilterOptions}
              value={filterCompany}
              onValueChange={({ value }) => setFilterCompany(value)}
              placeholder="Toutes les sociétés"
              label="Société"
            />
            <Select
              options={roleOptions}
              value={filterRole}
              onValueChange={({ value }) => setFilterRole(value)}
              placeholder="Tous les rôles"
              label="Rôle"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            Chargement...
          </div>
        ) : filteredEmployees.length === 0 && newRows.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            {search ? "Aucun résultat pour cette recherche" : "Aucune donnée"}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                {/* Variable columns share the space left over once the fixed
                    columns (Rôle 9rem, Statut/Actions 7rem each = 23rem) are
                    reserved, proportionally to their minimum widths (spec:
                    Nom 20, Email 50, Société 25 caractères, total 95). Plain
                    percentages next to px-fixed columns don't sum to 100% in
                    a table-fixed layout and overflow the container. */}
                <col style={{ width: "calc((100% - 23rem) * 20 / 95)" }} />
                <col style={{ width: "calc((100% - 23rem) * 50 / 95)" }} />
                <col style={{ width: "calc((100% - 23rem) * 25 / 95)" }} />
                <col className="w-36" />
                <col className="w-28" />
                <col className="w-28" />
              </colgroup>
              <thead>
                <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
                  <th className="truncate p-2 font-medium">Nom</th>
                  <th className="truncate p-2 font-medium">Email</th>
                  <th className="truncate p-2 font-medium">Société</th>
                  <th className="truncate p-2 font-medium">Rôle</th>
                  <th className="truncate p-2 font-medium">Statut</th>
                  <th className="truncate p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => {
                  const d = getDraft(employee);
                  const dirty = isDirtyRow(employee);
                  return (
                    <tr
                      key={employee.id}
                      className={cn(
                        "border-t border-zinc-100 dark:border-zinc-800",
                        dirty && "bg-amber-50 dark:bg-amber-900/20",
                      )}
                    >
                      <td className="p-2">
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={d.firstName}
                            onChange={(e) =>
                              onFieldChange(employee, {
                                firstName: e.target.value,
                              })
                            }
                            className={inputClass(dirty)}
                            placeholder="Prénom"
                          />
                          <input
                            type="text"
                            value={d.lastName}
                            onChange={(e) =>
                              onFieldChange(employee, {
                                lastName: e.target.value.toUpperCase(),
                              })
                            }
                            className={inputClass(dirty)}
                            placeholder="Nom"
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <input
                          type="email"
                          value={d.email}
                          onChange={(e) =>
                            onFieldChange(employee, { email: e.target.value })
                          }
                          className={inputClass(dirty)}
                          placeholder="email@societe.fr (facultatif)"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          flat
                          options={allCompanyOptions}
                          value={d.companyId ? [String(d.companyId)] : []}
                          onValueChange={(details) => {
                            const val = details.value[0];
                            onFieldChange(employee, {
                              companyId: val ? Number(val) : 0,
                            });
                          }}
                          placeholder="Société"
                        />
                      </td>
                      <td className="p-2">
                        {employee.role === "root" ? (
                          <Badge variant={roleColors.root}>
                            {EMPLOYEE_ROLE_LABELS.root}
                          </Badge>
                        ) : (
                          <Select
                            flat
                            options={assignableRoleOptions}
                            value={[roleSelectValue(d.role)]}
                            onValueChange={(details) => {
                              onFieldChange(employee, {
                                role: parseRoleSelectValue(details.value[0]),
                              });
                            }}
                          />
                        )}
                      </td>
                      <td className="p-2">
                        <Select
                          flat
                          options={statusOptions}
                          value={[d.isActive ? "1" : "0"]}
                          onValueChange={(details) => {
                            const val = details.value[0];
                            if (isNotNil(val)) {
                              onFieldChange(employee, {
                                isActive: val === "1",
                              });
                            }
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <Button
                            plain
                            onClick={() => handleEdit(employee)}
                            aria-label="Modifier (date d'entrée...)"
                          >
                            <PencilIcon className="size-4" />
                          </Button>
                          <Button
                            plain
                            onClick={() => handleDelete(employee)}
                            aria-label="Supprimer"
                          >
                            <TrashIcon className="size-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {newRows.map((row) => (
                  <tr
                    key={row.tempId}
                    className="border-t border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
                  >
                    <td className="p-2">
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={row.firstName}
                          onChange={(e) =>
                            onNewRowChange(row.tempId, {
                              firstName: e.target.value,
                            })
                          }
                          className={inputClass(true)}
                          placeholder="Prénom"
                        />
                        <input
                          type="text"
                          value={row.lastName}
                          onChange={(e) =>
                            onNewRowChange(row.tempId, {
                              lastName: e.target.value.toUpperCase(),
                            })
                          }
                          className={inputClass(true)}
                          placeholder="Nom"
                        />
                      </div>
                    </td>
                    <td className="p-2">
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) => {
                          const email = e.target.value;
                          onNewRowChange(row.tempId, {
                            email,
                            // Preselect the role from the email domain until
                            // the user picks one explicitly.
                            ...(row.roleTouched
                              ? {}
                              : { role: defaultRoleForEmail(email) }),
                          });
                        }}
                        className={inputClass(true)}
                        placeholder="email@societe.fr (facultatif)"
                      />
                    </td>
                    <td className="p-2">
                      <Select
                        flat
                        options={allCompanyOptions}
                        value={row.companyId ? [String(row.companyId)] : []}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          onNewRowChange(row.tempId, {
                            companyId: val ? Number(val) : 0,
                          });
                        }}
                        placeholder="Société"
                      />
                    </td>
                    <td className="p-2">
                      <Select
                        flat
                        options={assignableRoleOptions}
                        value={[roleSelectValue(row.role)]}
                        onValueChange={(details) => {
                          onNewRowChange(row.tempId, {
                            role: parseRoleSelectValue(details.value[0]),
                            roleTouched: true,
                          });
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <Select
                        flat
                        options={statusOptions}
                        value={[row.isActive ? "1" : "0"]}
                        onValueChange={(details) => {
                          const val = details.value[0];
                          if (isNotNil(val)) {
                            onNewRowChange(row.tempId, {
                              isActive: val === "1",
                            });
                          }
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <Button
                        plain
                        onClick={() =>
                          setNewRows((prev) =>
                            prev.filter((r) => r.tempId !== row.tempId),
                          )
                        }
                        aria-label="Annuler l'ajout du collaborateur"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AdminListPagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <EmployeeForm
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        employee={
          editingEmployee
            ? {
                id: editingEmployee.id,
                companyId: editingEmployee.companyId,
                email: editingEmployee.email,
                firstName: editingEmployee.firstName,
                lastName: editingEmployee.lastName,
                role: editingEmployee.role,
                isActive: editingEmployee.isActive,
                dateJoined: editingEmployee.dateJoined,
              }
            : undefined
        }
        onSuccess={() => refetch()}
      />

      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir supprimer l'employé "
                  {[deletingEmployee?.firstName, deletingEmployee?.lastName]
                    .filter(Boolean)
                    .join(" ") || "Sans nom"}
                  " ? Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type EmployeesListProps = Record<string, never>;
